/* eslint-disable no-console */

// Detects a new deployment by comparing the version baked into this bundle with
// /version.json on the server (emitted by vue.config.js), then hard-refreshes:
// clears Cache Storage, unregisters any service worker and reloads from network.
//
// - On app start a mismatch reloads immediately (nothing to lose yet).
// - Mid-session it only marks an update as pending and reloads on the next
//   route change, so users don't lose half-filled forms.

const CURRENT_VERSION = process.env.VUE_APP_BUILD_VERSION || "";
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
// Guards against reload loops (e.g. CDN still serving the old index.html):
// we only hard-reload once per target version per browser session.
const RELOAD_TARGET_KEY = "versionReloadTarget";
const CHUNK_RELOAD_AT_KEY = "chunkErrorReloadAt";

let updatePending = false;
let reloading = false;
let lastTargetPath = null;

async function fetchServerVersion() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.version ? String(data.version) : null;
  } catch (err) {
    return null;
  }
}

export async function hardReload(targetPath) {
  if (reloading) return;
  reloading = true;
  try {
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
  } catch (err) {
    console.error("Failed to clear caches before reload:", err);
  }
  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  if (targetPath && targetPath !== currentPath) {
    window.location.assign(targetPath);
  } else {
    window.location.reload();
  }
}

async function checkForNewVersion(reloadNow) {
  if (updatePending || reloading) return;
  const serverVersion = await fetchServerVersion();
  if (!serverVersion || serverVersion === CURRENT_VERSION) {
    sessionStorage.removeItem(RELOAD_TARGET_KEY);
    return;
  }
  if (sessionStorage.getItem(RELOAD_TARGET_KEY) === serverVersion) {
    // Already hard-reloaded for this version and still running old code; don't loop.
    return;
  }
  sessionStorage.setItem(RELOAD_TARGET_KEY, serverVersion);
  console.log(`New version available (${CURRENT_VERSION} -> ${serverVersion}).`);
  if (reloadNow) {
    await hardReload();
  } else {
    updatePending = true;
  }
}

function isChunkLoadError(err) {
  const message = (err && (err.message || String(err))) || "";
  return (
    (err && err.name === "ChunkLoadError") ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

export function installVersionCheck(router) {
  if (process.env.NODE_ENV !== "production" || !CURRENT_VERSION) return;

  checkForNewVersion(true);

  setInterval(() => checkForNewVersion(false), CHECK_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForNewVersion(false);
  });

  router.beforeEach((to, from, next) => {
    lastTargetPath = to.fullPath;
    // from.matched is empty on the initial navigation
    if (updatePending && from.matched.length > 0) {
      next(false);
      hardReload(to.fullPath);
      return;
    }
    next();
  });

  // Old bundle asking for lazy chunks that no longer exist after a deploy
  router.onError((err) => {
    if (!isChunkLoadError(err)) return;
    // Avoid a reload loop when the chunk genuinely can't be fetched (e.g. offline)
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY) || 0);
    if (Date.now() - lastReload < 30 * 1000) return;
    sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()));
    hardReload(lastTargetPath || undefined);
  });
}
